import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import * as Yup from 'yup';
import omit from 'lodash/omit';
import times from 'lodash/times';
import moment from '@g4/moment-utc';

import { useLazySSRQuery } from '../graphql';
import { conditionallyRequired } from '../utils/propTypesUtils';
import { readStickyUtmSource } from '../utils/stickyUtmUtils';

import FLIGHT_SEARCH_CRITERIA_QUERY from './useFlightSearchCriteria/flightSearchCriteria.query.graphql.js';

export const tripTypeValues = {
  ROUNDTRIP: 'ROUNDTRIP',
  ONEWAY: 'ONEWAY',
};

export const propTypes = PropTypes.shape({
  tripType: PropTypes.oneOf(Object.keys(tripTypeValues)).isRequired,
  origin: PropTypes.string.isRequired,
  destination: PropTypes.string.isRequired,
  departDate: PropTypes.shape({
    date: PropTypes.string.isRequired,
  }).isRequired,
  returnDate: conditionallyRequired(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
    }),
    props => props.tripType === tripTypeValues.ROUNDTRIP
  ),
  adultsCount: PropTypes.number,
  infantCount: PropTypes.number,
  childrenCount: PropTypes.number,
  lapInfantCount: PropTypes.number,
  utmSource: PropTypes.string,
  utmMedium: PropTypes.string,
  utmCampaign: PropTypes.string,
});

const fieldNames = {
  TRIP_TYPE: 'tt',
  ORIGIN: 'o',
  DESTINATION: 'd',
  ADULTS: 'ta',
  CHILDREN: 'tc',
  INFANTS_IN_LAP: 'til',
  INFANTS_IN_SEAT: 'tis',
  DEPARTING_DATE: 'ds',
  RETURNING_DATE: 'de',
  UTM_SOURCE: 'utm_source',
  UTM_MEDIUM: 'utm_medium',
  UTM_CAMPAIGN: 'utm_campaign',
  // WITH_CARS: 'c',
  // WITH_HOTELS: 'h',
};

const validationSchema = Yup.object().shape({
  [fieldNames.TRIP_TYPE]: Yup.string()
    .oneOf(Object.values(tripTypeValues))
    .required(),
  [fieldNames.ORIGIN]: Yup.string().required(),
  [fieldNames.DESTINATION]: Yup.string().required(),
  [fieldNames.DEPARTING_DATE]: Yup.string().required(),
  [fieldNames.RETURNING_DATE]: Yup.string().when(fieldNames.TRIP_TYPE, {
    is: fieldValue => fieldValue === tripTypeValues.ROUNDTRIP,
    then: Yup.string().required(),
  }),
  [fieldNames.ADULTS]: Yup.number().default(1),
  [fieldNames.CHILDREN]: Yup.number().default(0),
  [fieldNames.INFANTS_IN_LAP]: Yup.number().default(0),
  [fieldNames.INFANTS_IN_SEAT]: Yup.number().default(0),
  [fieldNames.UTM_SOURCE]: Yup.string().optional(),
  [fieldNames.UTM_MEDIUM]: Yup.string().optional(),
  [fieldNames.UTM_CAMPAIGN]: Yup.string().optional(),
  // [fieldNames.WITH_CARS]: Yup.bool().default(false),
  // [fieldNames.HOTELS]: Yup.bool().default(false),
});

export const flightSearchCriteriaToQueryParams = flightSearchCriteria => {
  if (!flightSearchCriteria) return {};

  const params = {};

  if (flightSearchCriteria.tripType) params[fieldNames.TRIP_TYPE] = flightSearchCriteria.tripType;
  if (flightSearchCriteria.origin) params[fieldNames.ORIGIN] = flightSearchCriteria.origin;
  if (flightSearchCriteria.destination) params[fieldNames.DESTINATION] = flightSearchCriteria.destination;
  if (flightSearchCriteria.departDate?.date) params[fieldNames.DEPARTING_DATE] = flightSearchCriteria.departDate.date;
  if (flightSearchCriteria.returnDate?.date) params[fieldNames.RETURNING_DATE] = flightSearchCriteria.returnDate.date;
  if (flightSearchCriteria.adultsCount !== undefined) params[fieldNames.ADULTS] = flightSearchCriteria.adultsCount;
  if (flightSearchCriteria.childrenCount !== undefined) params[fieldNames.CHILDREN] = flightSearchCriteria.childrenCount;
  if (flightSearchCriteria.lapInfantCount !== undefined) params[fieldNames.INFANTS_IN_LAP] = flightSearchCriteria.lapInfantCount;
  if (flightSearchCriteria.infantCount !== undefined) params[fieldNames.INFANTS_IN_SEAT] = flightSearchCriteria.infantCount;
  if (flightSearchCriteria.utmSource) params[fieldNames.UTM_SOURCE] = flightSearchCriteria.utmSource;
  if (flightSearchCriteria.utmMedium) params[fieldNames.UTM_MEDIUM] = flightSearchCriteria.utmMedium;
  if (flightSearchCriteria.utmCampaign) params[fieldNames.UTM_CAMPAIGN] = flightSearchCriteria.utmCampaign;

  return params;
};

export const queryParamsToFlightSearchCriteria = (
  queryParams,
  resolvedUtmSource
) => {
  // Remove unknown query params and cast query params to correct types (i.e. number strings to ints)
  const validatedQueryParams = validationSchema.validateSync(queryParams, {
    stripUnknown: true,
  });
  // Construct flight search criteria from query params
  return {
    tripType: validatedQueryParams[fieldNames.TRIP_TYPE],
    origin: validatedQueryParams[fieldNames.ORIGIN],
    destination: validatedQueryParams[fieldNames.DESTINATION],
    departDate: {
      date: validatedQueryParams[fieldNames.DEPARTING_DATE],
      plusDays: 0,
      minusDays: 0,
    },
    ...(validatedQueryParams[fieldNames.TRIP_TYPE] !== tripTypeValues.ONEWAY
      ? {
        returnDate: {
          date: validatedQueryParams[fieldNames.RETURNING_DATE],
          plusDays: 0,
          minusDays: 0,
        },
      }
      : {}),
    adultsCount: validatedQueryParams[fieldNames.ADULTS],
    infantCount: validatedQueryParams[fieldNames.INFANTS_IN_SEAT],
    childrenCount: validatedQueryParams[fieldNames.CHILDREN],
    lapInfantCount: validatedQueryParams[fieldNames.INFANTS_IN_LAP],
    // HACK: This **ISN'T** the dobs of infants in laps
    // The array length is separate from the count of lap infants
    // and is instead used to store the number of *seated* infants
    lapInfantDobs: times(validatedQueryParams[fieldNames.INFANTS_IN_SEAT], () =>
      moment().format('YYYY-MM-DD')
    ),
    // Add fields to capture search context from referral deeplink URL parameters
    utmSource:
      validatedQueryParams[fieldNames.UTM_SOURCE] || resolvedUtmSource,
    utmMedium: validatedQueryParams[fieldNames.UTM_MEDIUM],
    utmCampaign: validatedQueryParams[fieldNames.UTM_CAMPAIGN],
  };
};

const useFlightSearchCriteria = () => {
  const { query = {} } = useRouter();

  // A utm_source in the URL always wins and the persisted cookie is only a fallback
  const resolvedUtmSource =
    query[fieldNames.UTM_SOURCE] || readStickyUtmSource() || undefined;

  const [
    fetchFlightSearchCriteria,
    flightSearchCriteriaQueryResponse,
  ] = useLazySSRQuery(FLIGHT_SEARCH_CRITERIA_QUERY, {
    fetchPolicy: 'cache-first',
  });

  // Return processed query params if the required search query params are valid
  if (validationSchema.isValidSync(query)) {
    return {
      data: queryParamsToFlightSearchCriteria(query, resolvedUtmSource),
    };
  }

  // Return the GraphQL query response if it has been called
  if (flightSearchCriteriaQueryResponse.called) {
    const { data, loading, error, client } = flightSearchCriteriaQueryResponse;

    const invalidateFlightSearchCriteriaCache = () => {
      Object.keys(client.cache.data.data).forEach(
        key =>
          key.indexOf('flightSearchCriteria') >= 0 &&
          client.cache.data.delete(key)
      );
    };

    let flightSearchCriteria;

    if (data) {
      const flightOrderItems = data.order?.items.filter(
        item => item.__typename === 'FlightOrderItem'
      );

      // Use flight dates from flights added to cart if they are present
      if (flightOrderItems?.length) {
        const sortedFlightOrderItems = [...flightOrderItems].sort((a, b) =>
          moment(a.flight.departingTime).diff(moment(b.flight.departingTime))
        );

        const getFlightDate = flightOrderItem => {
          if (!flightOrderItem) {
            return undefined;
          }

          return {
            date: moment(flightOrderItem.flight.departingTime).format(
              'YYYY-MM-DD'
            ),
            plusDays: 0,
            minusDays: 0,
          };
        };

        flightSearchCriteria = {
          ...data.flightSearchCriteria,
          departDate: getFlightDate(sortedFlightOrderItems[0]),
          returnDate: getFlightDate(sortedFlightOrderItems[1]),
        };
      } else {
        flightSearchCriteria = data.flightSearchCriteria;
      }
    }

    // Remove `__typename` field as this object is used as the input to the `flights` query
    if (flightSearchCriteria) {
      return {
        // This query returns no utm fields, so re-apply the persisted value.
        data: {
          ...omit(flightSearchCriteria, '__typename'),
          ...(resolvedUtmSource ? { utmSource: resolvedUtmSource } : {}),
        },
        loading,
        error,
        invalidateFlightSearchCriteriaCache,
      };
    } else {
      return data ? { error: 'No search criteria found' } : { loading: true };
    }
  }

  // Call the GraphQL query if a `transactionId` query param is present
  if (query.transactionId) {
    fetchFlightSearchCriteria();
    return { loading: true };
  }

  // Return redirect with valid query params if in development
  if (process.env.NODE_ENV === 'development') {
    if (typeof window !== 'undefined') {
      const date = moment()
        .add(7, 'days')
        .format('YYYY-MM-DD');

      window.location.href = `/booking/flights?tt=ONEWAY&o=BLI&d=LAS&ta=1&tc=0&tis=0&til=0&ds=${date}&c=0&h=0`;
    }

    return { loading: true };
  }

  return {
    error: 'No search criteria found',
  };
};

export default useFlightSearchCriteria;